import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import MuiLoader from 'components/Common/Loader';
import ProfileItemDetail from 'components/ProfileItemDetail';
import Routes from 'constants/routes';
import { useNavigate } from 'react-router-dom';
import { dateFormat, debounce } from 'utils/helpers';
import TextInput from 'components/Common/TextInput';
import MenuList from 'components/Common/MenuList';
import { defaultPageSize } from 'appConfig';

interface PaymentTokenListProps {
    loadData: any;
}

const PaymentTokenList: React.FC<PaymentTokenListProps> = ({ loadData }: any) => {
    const navigate = useNavigate();
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DSC'>('ASC');

    const containerRef = useRef<HTMLDivElement | null>(null);

    const debouncedSetSearchText = useCallback(
        debounce((value: string) => {
            setDebouncedSearchText(value);
        }, 700),
        []
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSetSearchText(value);
    };

    const fetchData = async (reset = false) => {
        setLoading(true);

        const newOffset = reset ? 0 : offset;
        const newData = await loadData(newOffset, defaultPageSize, debouncedSearchText, sortOrder);

        setData((prev) => (reset ? newData : [...prev, ...newData]));
        setOffset((prev) => (reset ? defaultPageSize : prev + defaultPageSize));

        if (newData.length < defaultPageSize) {
            setHasMore(false);
        } else {
            setHasMore(true);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchData(true);
    }, [debouncedSearchText, sortOrder]);

    useEffect(() => {
        const handleScroll = () => {
            const el = containerRef.current;
            if (!el) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10 && !loading && hasMore) {
                fetchData();
            }
        };
        const el = containerRef.current;
        el?.addEventListener('scroll', handleScroll);
        return () => el?.removeEventListener('scroll', handleScroll);
    }, [loading, hasMore]);

    const navigationHandler = (id: any) => {
        if (!id) return;
        navigate(Routes.account.paymentTokenDetail.replace(':id', id));
    };

    return (
        <>
            <Stack direction="row" justifyContent={'space-between'} alignItems="center" mb={'5%'}>
                <TextInput
                    placeholder="Search by nickname"
                    sx={{
                        width: {
                            xs: '100%',
                            md: '50%',
                        },
                    }}
                    value={searchText}
                    onChange={handleSearchChange}
                />
                <MenuList
                    menuItems={[
                        {
                            item: 'ASC',
                            onClick: () => setSortOrder('ASC'),
                            disabled: sortOrder === 'ASC' || !data?.length,
                        },
                        {
                            item: 'DSC',
                            onClick: () => setSortOrder('DSC'),
                            disabled: sortOrder === 'DSC' || !data?.length,
                        },
                    ]}
                />
            </Stack>
            {loading && <MuiLoader absolute={true} loading={loading} />}
            <div
                ref={containerRef}
                style={{
                    maxHeight: '500px',
                    overflowY: 'auto',
                }}
            >
                {data?.length ? (
                    data.map((authResponse, index) => (
                        <Stack
                            key={index}
                            onClick={() => navigationHandler(authResponse?.id)}
                            sx={{
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                            }}
                        >
                            <ProfileItemDetail label={authResponse?.nickname} value={dateFormat(authResponse?.updatedAt)} />
                        </Stack>
                    ))
                ) : !loading ? (
                    <Typography textAlign={'center'} alignSelf={'center'}>
                        {' '}
                        No tokens available.{' '}
                    </Typography>
                ) : null}
            </div>
        </>
    );
};

export default PaymentTokenList;
